import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  motion,
  useMotionValueEvent,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";

/**
 * SCENE — ein Akt der filmischen Scroll-Timeline.
 *
 * Scene-Stacking: Jede Szene ist sticky und „klebt", nachdem sie komplett
 * durchscrollt wurde, während die nächste Szene über sie hinwegzieht. Die
 * verdeckte Szene löst sich scrub-gesteuert in der Tiefe auf (Fade + Scale +
 * rotateX Richtung Dunkelheit) — vorwärts wie rückwärts (reversibel).
 * Bewusst KEIN CSS-filter pro Frame (rasterisiert die ganze Section neu →
 * Ruckeln auf Mobile); nur GPU-komponierte Transforms + Opacity.
 *
 * Lange Szenen kleben bottom-aligned (top = vh - h), kurze zentriert.
 * Keine React-Rerenders pro Frame: nur MotionValues + direkte Style-Writes.
 * Reduced-Motion: komplett statisch.
 */

// Nächste echte Szene im DOM (fixed/absolute/winzig kleine Elemente überspringen)
function findNextSceneSibling(el) {
  let sib = el?.nextElementSibling || null;
  while (sib) {
    const pos = window.getComputedStyle(sib).position;
    if (pos !== "fixed" && pos !== "absolute" && sib.offsetHeight >= 10) return sib;
    sib = sib.nextElementSibling;
  }
  return null;
}

export default function Scene({ children, name, z = 1, last = false, enter = true }) {
  const rootRef = useRef(null);
  const nextRef = useRef(null);
  const [stickTop, setStickTop] = useState(0);
  const reduced = useReducedMotion();

  // Exit-Treiber auflösen (Layout-Phase: läuft vor framer's useScroll-Effekt)
  useLayoutEffect(() => {
    nextRef.current = findNextSceneSibling(rootRef.current);
  });

  // Sticky-Punkt messen: ResizeObserver + resize + Nachmessungen (Bilder/Fonts)
  useEffect(() => {
    if (reduced) return undefined;
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => {
      const h = el.offsetHeight;
      const vh = window.innerHeight;
      setStickTop(h >= vh ? vh - h : Math.round((vh - h) / 2));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener("resize", measure, { passive: true });
    const t1 = setTimeout(measure, 800);
    const t2 = setTimeout(measure, 2400);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measure);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reduced]);

  // AUSTTRITT: 0 = nächste Szene betritt den Viewport, 1 = füllt ihn fast.
  // („start 0.15" statt „start start": auch kurze, zentriert klebende
  // Nachfolger erreichen den Endwert zuverlässig.)
  // Die letzte Szene (Footer/Impressum) hat KEINEN Exit: Sie bleibt am
  // Seitenende voll sichtbar stehen — kein „schwarzes Blatt" beim Ausscrollen.
  const { scrollYProgress: exitRaw } = useScroll({
    target: last ? rootRef : nextRef,
    offset: ["start end", "start 0.15"],
  });
  const exitP = useTransform(exitRaw, (v) => (last ? 0 : v));
  const exitOpacity = useTransform(exitP, [0, 0.55, 1], [1, 0.3, 0]);
  const exitScale = useTransform(exitP, [0, 1], [1, 0.9]);
  const exitY = useTransform(exitP, [0, 1], [0, -60]);
  const exitRotateX = useTransform(exitP, [0, 1], [0, -8]);

  // EINTRITT: Szene setzt sich in den ersten 30 % des Hereinfahrens zusammen
  const { scrollYProgress: enterP } = useScroll({
    target: rootRef,
    offset: ["start end", "start start"],
  });
  const enterOpacity = useTransform(enterP, [0, 0.3], [0, 1]);
  const enterY = useTransform(enterP, [0, 0.3], [60, 0]);
  const enterRotateX = useTransform(enterP, [0, 0.3], [5, 0]);

  // Verdeckte Szenen: nicht klickbar, nicht gemalt (beides reversibel)
  useMotionValueEvent(exitP, "change", (v) => {
    const el = rootRef.current;
    if (!el) return;
    el.style.pointerEvents = !enter || v > 0.45 ? "none" : "";
    el.style.visibility = v >= 0.995 ? "hidden" : "";
  });

  // Reduced Motion: statische, voll sichtbare Reihenfolge
  if (reduced) {
    return (
      <div ref={rootRef} data-testid={`scene-${name}`} className="relative" style={{ zIndex: z }}>
        {children}
      </div>
    );
  }

  return (
    <div
      ref={rootRef}
      data-testid={`scene-${name}`}
      className="sticky"
      // Kapitel-Titel (enter=false) sind nicht interaktiv: Klicks gehen durch
      // zur Szene darunter, die sie beim Hereinfahren überlagern.
      style={{ top: stickTop, zIndex: z, pointerEvents: enter ? undefined : "none" }}
    >
      {/* Exit-Transform (Origin unten: die Szene sinkt in die Tiefe weg) */}
      <motion.div
        style={{
          opacity: exitOpacity,
          scale: exitScale,
          y: exitY,
          rotateX: exitRotateX,
          transformPerspective: 1400,
          transformOrigin: "50% 100%",
          willChange: "transform, opacity",
        }}
      >
        {enter ? (
          /* Eintritts-Transform (Origin oben: Kante fährt ein, Rest kippt nach) */
          <motion.div
            style={{
              opacity: enterOpacity,
              y: enterY,
              rotateX: enterRotateX,
              transformPerspective: 1400,
              transformOrigin: "50% 0%",
            }}
          >
            {children}
          </motion.div>
        ) : (
          children
        )}
      </motion.div>
    </div>
  );
}
